import React from 'react';
import { DynamicSvg } from '../DynamicSvg';
import isNil from 'lodash-es/isNil';
import { useChartShapes, useChartUIContext } from '@/contexts';
import { ShapeType } from '@/types/commontypes';
import { useGlobalShapeSelector } from '@/contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '@/stores/chartUIStore';

interface YCoordinatePopupProps {
  showOrderMenu?: boolean;
}

export const YCoordinatePopup: React.FC<YCoordinatePopupProps> = ({ showOrderMenu = false }) => {
  const { chartUIStore } = useChartUIContext();
  const yCoordinateValue = useChartUIStoreSelector(chartUIStore, s => s.yCoordinateValue);
  const activePopup = useChartUIStoreSelector(chartUIStore, s => s.activePopup);
  const setActivePopup = useChartUIStoreSelector(chartUIStore, s => s.setActivePopup);
  const setShapeTypeToDraw = useGlobalShapeSelector(s => s.setShapeTypeToDraw);
  const { drawState, setDrawState, renderNodes } = useChartShapes();
  const setEnableShapeSelection = useGlobalShapeSelector(s => s.setEnableShapeSelection);
  const setYCoordinateClicked = useChartUIStoreSelector(chartUIStore, s => s.setYCoordinateClicked);
  const hedgeMode = useGlobalShapeSelector(s => s.hedgeMode);

  const activeChartId = activePopup?.type === 'y-popup' ? activePopup.chartId : '';

  const conflictResult = React.useMemo(() => {
    if (!showOrderMenu || !activeChartId) return null;
    const orderLines = renderNodes[activeChartId]?.[ShapeType.ORDER_LINE] ?? [];
    const hasLong = orderLines.some((n: unknown) => (n as Record<string, unknown>)['positionType'] === 'long');
    const hasShort = orderLines.some((n: unknown) => (n as Record<string, unknown>)['positionType'] === 'short');
    if (!hedgeMode) {
      const blocked = hasLong || hasShort;
      return { canEnterLong: !blocked, canEnterShort: !blocked, canEnterHedge: false };
    }
    if (hasLong && hasShort) return { canEnterLong: false, canEnterShort: false, canEnterHedge: false };
    if (hasLong) return { canEnterLong: false, canEnterShort: true, canEnterHedge: false };
    if (hasShort) return { canEnterLong: true, canEnterShort: false, canEnterHedge: false };
    return { canEnterLong: true, canEnterShort: true, canEnterHedge: true };
  }, [showOrderMenu, renderNodes, activeChartId, hedgeMode]);

  if (!activePopup || activePopup.type !== 'y-popup') return null;

  const { chartId, coords } = activePopup; // shared fields

  const onDrawHorizontalLine = () => {
    if (isNil(yCoordinateValue)) return;
    setYCoordinateClicked({ chartId: chartId, value: yCoordinateValue });
    setEnableShapeSelection(true);
    setActivePopup(null);
    setShapeTypeToDraw(ShapeType.HORZ_LINE);
    setDrawState({ ...drawState, chartId: chartId });
  };

  const onPlaceOrder = (position: 'long' | 'short' | 'hedge') => {
    setActivePopup({
      type: 'order-popup',
      coords: coords,
      chartId: chartId,
      position,
    });
  };

  return (
    <>
      <div
        className="absolute bg-white border border-gray-300 rounded z-999 min-w-[180px] shadow-md"
        style={{ top: coords.y, left: coords.x }}
      >
        <div className="flex flex-col">
          {showOrderMenu && (
            <>
              {conflictResult?.canEnterLong !== false && (
                <button
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition"
                  onClick={() => onPlaceOrder('long')}
                >
                  <span className="text-2xl text-gray-700">+</span>
                  <span className="text-sm text-gray-800 font-small">Place Long Order</span>
                </button>
              )}

              {conflictResult?.canEnterShort !== false && (
                <button
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition"
                  onClick={() => onPlaceOrder('short')}
                >
                  <span className="text-2xl text-gray-700">+</span>
                  <span className="text-sm text-gray-800 font-small">Place Short Order</span>
                </button>
              )}

              {hedgeMode && conflictResult?.canEnterHedge !== false && (
                <button
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition"
                  onClick={() => onPlaceOrder('hedge')}
                >
                  <span className="text-2xl text-gray-700">+</span>
                  <span className="text-sm text-gray-800 font-small">Place Hedge Order</span>
                </button>
              )}
            </>
          )}

          <button
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 transition"
            onClick={onDrawHorizontalLine}
          >
            <DynamicSvg iconName="HorzLine" title="HorzLine" className="w-6 h-6" />
            <span className="text-sm text-gray-800 font-small">
              Draw Horizontal Line at {yCoordinateValue?.toFixed(2) ?? ''}
            </span>
          </button>
        </div>
      </div>
    </>
  );
};
